<script setup lang="ts">
import { INQUIRY_ITEM, INQUIRY_STATUS } from '@/types'
import { highlightInquiry } from '@/utils/highlightInquiry'
import { matchHighlightedInquiryStatus } from '@/utils/matchers'
import { formatDistance, parseISO } from 'date-fns'
import { useElementVisibility } from '@vueuse/core'
import { sendShownNextStepsEvent } from '@/utils/mixpanel'
import { matchStatusToIcon } from '@/utils/document-status-icon-matchers'

const props = defineProps<INQUIRY_ITEM>()
const inquiriesStore = useInquiriesStore()
const appStore = useAppStore()
const respondText = ref(null)
const followUpText = ref(null)
const showDocumentsPopUp = ref(false)

const { showRespond, showFollowUp } = highlightInquiry(props, inquiriesStore)

const respondTextIsVisible = useElementVisibility(respondText)
const followUpTextIsVisible = useElementVisibility(followUpText)

const inquiryStatus = computed(() => {
  return (
    props.status || props.pick_up_order_status || props.delivery_order_status
  )
})

const hasMessages = computed(() => {
  return props.get_num_of_unread_emails > 0
})

const isHighlighted = computed(() => {
  if (
    (props.type === 'Private Party' || props.type === 'Catering') &&
    (props.status === 'waiting on customer' ||
      props.status === 'autoresponse sent')
  ) {
    return true
  }
  return (
    matchHighlightedInquiryStatus(inquiryStatus.value as INQUIRY_STATUS) ||
    hasMessages.value
  )
})

const timeAgo = computed(() => {
  return formatDistance(parseISO(props.created_at), new Date(), {
    addSuffix: true,
  })
    .replace('about ', '')
    .replace('less than a minute ago', 'just now')
})

watch(followUpTextIsVisible, (value) => {
  if (value) {
    sendShownNextStepsEvent(props, 'Follow-up')
  }
})

watch(respondTextIsVisible, (value) => {
  if (value) {
    sendShownNextStepsEvent(props, 'Respond')
  }
})

const handleShowDocumentsPopUp = () => {
  showDocumentsPopUp.value = true
}
</script>

<template>
  <div class="flex h-full flex-col justify-between text-right">
    <div
      v-if="isHighlighted"
      class="flex min-h-[50%] gap-1"
      :class="[
        {
          'flex-col': props.type == 'Private Party' || props.type == 'Catering',
        },
        {
          'justify-end':
            props.type != 'Private Party' && props.type != 'Catering',
        },
      ]"
    >
      <p class="text-sm uppercase text-blue-600">
        <span>{{ inquiryStatus }}</span>
      </p>

      <button
        v-if="
          props.documents &&
          props.documents.length > 0 &&
          appStore.spotInfo.pandadoc_enabled
        "
        class="mb-3 mt-1 w-fit self-end rounded-2xl bg-[#f3f7fb] px-3 py-1 text-xs uppercase shadow-[1px_1px_5px_0_rgba(0,0,0,0.3);]"
        @click.stop="handleShowDocumentsPopUp()"
      >
        <span
          v-if="props.documents.length === 1"
          class="flex items-center gap-2"
        >
          <img
            :src="matchStatusToIcon(props.documents[0].status)"
            :alt="`${props.documents[0].status} icon`"
            class="h-3.5 w-4 grayscale"
          />
          {{ props.documents[0].status }}
        </span>

        <span v-else class="flex items-center gap-2">
          {{ props.documents.length }}
          contracts
          <img
            src="https://spothopper-static.s3.amazonaws.com/assets/admin/arrow_down_triangle.png"
            alt="arrow down"
            class="h-2 w-auto"
          />
        </span>
      </button>

      <div
        v-if="props.type == 'Private Party' || props.type == 'Catering'"
        class="flex items-center gap-2 self-end"
      >
        <AppiconArrowRight
          v-show="showRespond || showFollowUp"
          class="z-[-1] h-4 animate-next-steps-animation fill-brand-primary"
        />
        <p
          v-show="showRespond"
          ref="respondText"
          class="text-sm font-medium uppercase text-brand-primary"
        >
          Respond
        </p>
        <p
          v-show="showFollowUp"
          ref="followUpText"
          class="text-sm font-medium uppercase text-brand-primary"
        >
          Follow-up
        </p>
      </div>
      <AppiconArrowRight
        v-else
        class="z-[-1] h-4 animate-left-right fill-blue-600"
      />
    </div>

    <p v-else class="justify-end text-sm uppercase text-slate-600">
      <span>{{ inquiryStatus }}</span>
      <button
        v-if="
          props.documents &&
          props.documents.length > 0 &&
          appStore.spotInfo.pandadoc_enabled
        "
        class="mb-3 mt-1 w-fit self-end rounded-2xl bg-[#f3f7fb] px-3 py-1 text-xs uppercase shadow-[1px_1px_5px_0_rgba(0,0,0,0.3);]"
        @click.stop="handleShowDocumentsPopUp()"
      >
        <span
          v-if="props.documents.length === 1"
          class="flex items-center gap-2"
        >
          <img
            :src="matchStatusToIcon(props.documents[0].status)"
            :alt="`${props.documents[0].status} icon`"
            class="h-3.5 w-4 grayscale"
          />
          {{ props.documents[0].status }}
        </span>

        <span v-else class="flex items-center gap-2">
          {{ props.documents.length }}
          contracts
          <img
            src="https://spothopper-static.s3.amazonaws.com/assets/admin/arrow_down_triangle.png"
            alt="arrow down"
            class="h-2 w-auto"
          />
        </span>
      </button>
    </p>

    <div class="flex items-center justify-end gap-1">
      <AppiconChat v-if="hasMessages" class="h-4 fill-blue-600" />
      <p class="text-sm text-slate-600">{{ timeAgo }}</p>
    </div>
  </div>

  <AppDialog
    :is-open="showDocumentsPopUp"
    :can-close="true"
    :has-close-button="true"
    @close="showDocumentsPopUp = false"
  >
    <template #content>
      <div class="px-5 pb-5">
        <div v-if="props.documents && props.documents.length === 1">
          <p>
            <b>Contract:</b>
            {{ props.documents[0].file_name }}
          </p>
          <span
            v-if="props.documents.length === 1"
            class="mr-3 mt-2 inline-flex w-fit items-center gap-2 self-end rounded-2xl bg-[#f3f7fb] px-3 py-1 text-[14px] capitalize shadow-[1px_1px_5px_0_rgba(0,0,0,0.3);]"
          >
            <img
              :src="matchStatusToIcon(props.documents[0].status)"
              :alt="`${props.documents[0].status} icon`"
              class="h-3.5 w-4"
            />
            {{ props.documents[0].status }}
          </span>
          <span class="text-[14px]">
            {{
              props.documents[0].updated_at ?? props.documents[0].date_modified
            }}
          </span>
        </div>
        <div v-else-if="props.documents">
          <div
            v-for="(doc, index) in props.documents"
            :key="index"
            class="pb-5"
          >
            <hr v-if="index > 0" class="mb-5" />
            <p>
              <b>Contract {{ index + 1 }}:</b>
              {{ doc.file_name }}
            </p>
            <span
              class="mr-3 mt-2 inline-flex w-fit items-center gap-2 self-end rounded-2xl bg-[#f3f7fb] px-3 py-1 text-[14px] capitalize shadow-[1px_1px_5px_0_rgba(0,0,0,0.3);]"
            >
              <img
                :src="matchStatusToIcon(doc.status)"
                :alt="`${doc.status} icon`"
                class="h-3.5 w-4"
              />
              {{ doc.status }}
            </span>
            <span class="text-[14px]">
              {{ doc.updated_at ?? doc.date_modified }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </AppDialog>
</template>
